// Data-shaping helpers for hurricane/tropical-storm API records.
// A "storm group" bundles every record (observed + forecast) for one storm.
import { getBoundedValue } from 'utils/math/math.utils';

// Longest names first so partial matches ("Tropical Storm" inside
// "Severe Tropical Storm") never win over the full type name.
const STORM_TYPE_LABELS = [
  'Severe Tropical Storm',
  'Tropical Depression',
  'Tropical Storm',
  'Hurricane',
  'Typhoon',
];

const getStormYear = (record) => record?.code?.split('_')?.[0] ?? '';

const getStormKey = (record) => `${record?.name ?? ''} (${getStormYear(record)})`;

const sortStormRecordsByTime = (records = []) =>
  [...records].sort((a, b) => new Date(a.utc_time) - new Date(b.utc_time));

export const getLatestStormRecord = (records = []) =>
  records.length ? records[records.length - 1] : null;

export const isForecastStormRecord = (record) =>
  String(record?.type ?? '')
    .trim()
    .toLowerCase() === 'forecast';

// A live storm feed can end with future forecast points. The normal map view
// should emphasize the latest measured position instead of treating the end
// of the forecast track as the storm's current location.
export const getLatestObservedStormRecordIndex = (records = []) => {
  for (let index = records.length - 1; index >= 0; index -= 1) {
    if (!isForecastStormRecord(records[index])) return index;
  }

  return records.length - 1;
};

// The record the UI should treat as "now": the replay frame when a timeline
// position is set, otherwise the storm's latest record.
export const getLastStormFrameIndex = (frameCount = 0) => Math.max(frameCount - 1, 0);

export const getStormRecordAtFrame = (records = [], frameIndex = null) => {
  if (!records.length) return null;

  const lastIndex = getLastStormFrameIndex(records.length);
  const activeIndex =
    frameIndex === null ? lastIndex : getBoundedValue(frameIndex, 0, lastIndex);
  return records[activeIndex];
};

export const groupStormRecords = (records = []) => {
  const recordsByKey = new Map();

  (records ?? []).forEach((record) => {
    if (!record?.name || !record?.code) return;
    const key = getStormKey(record);
    if (!recordsByKey.has(key)) recordsByKey.set(key, []);
    recordsByKey.get(key).push(record);
  });

  return [...recordsByKey.entries()]
    .map(([key, stormRecords]) => {
      const sortedRecords = sortStormRecordsByTime(stormRecords);
      const latestRecord = getLatestStormRecord(sortedRecords);

      return {
        key,
        name: latestRecord.name,
        localName: latestRecord.local_storm_name || latestRecord.name,
        year: getStormYear(latestRecord),
        records: sortedRecords,
      };
    })
    .sort(
      (a, b) =>
        parseInt(b.year, 10) - parseInt(a.year, 10) || a.localName.localeCompare(b.localName),
    );
};

const addStormTypeColon = (name = '') => {
  const matchedLabel = STORM_TYPE_LABELS.find((label) => name.includes(label));
  return matchedLabel ? name.replace(matchedLabel, `${matchedLabel}:`) : name;
};

export const getStormDisplayLabel = (storm) =>
  storm ? addStormTypeColon(`${storm.localName} (${storm.year})`) : '';

export const formatStormWindSpeed = (value) => {
  const windSpeed = parseFloat(value);
  return Number.isFinite(windSpeed) ? `${windSpeed.toFixed(1)} mph` : '--';
};

export const formatStormPressure = (value) => (value || value === 0 ? `${value} in.` : '--');

export const formatStormCoordinate = (value) => {
  const coordinate = parseFloat(value);
  return Number.isFinite(coordinate) ? `${coordinate}°` : '--';
};
